import { type PointerEvent as ReactPointerEvent, useCallback, useEffect, useMemo, useRef } from 'react';

/**
 * ÖFFNEN BEIM ÜBERFAHREN, MIT ABSICHT UND NACHLAUF (Auftrag #21, ÄA-41).
 *
 * Zwei Stellen öffnen etwas, wenn die Maus darüber steht: die Gruppen der
 * Menüzeile und der Hinweis am gesperrten Segmentschalter. Beide brauchen
 * dieselben zwei Uhren (die Maskenhilfe öffnet seit ÄA-46 nur auf Verlangen):
 *
 *   ABSICHT (120 ms). Wer mit der Maus quer über die Zeile fährt, will nichts
 *   öffnen. Erst wer kurz stehen bleibt, meint die Stelle.
 *
 *   NACHLAUF (250 ms). Zwischen Knopf und Liste liegt eine Lücke, und der Weg
 *   dorthin geht oft schräg über den Nachbarn. Schlösse die Liste beim ersten
 *   Verlassen, erreichte man sie nie. Wer in der Zeit zurückkommt, hält sie.
 *   In der Menüzeile endet er, sobald die Maus auf einem anderen Punkt der
 *   Zeile steht, mit Pfeil oder ohne (Auftrag CRM-49, `Aufklapper.tsx`).
 *
 * NUR EINE ECHTE MAUS (NFA-07). Ein Finger „überfährt" nichts: Auf dem Tablet
 * käme `pointerenter` mit dem Tippen zugleich, und das Tippen schlösse wieder,
 * was das Überfahren gerade geöffnet hat. Stift und Finger gehen deshalb den
 * Weg des Klicks wie bisher.
 */
export const ABSICHT_MS = 120;
export const NACHLAUF_MS = 250;

export function istMaus(e: Pick<ReactPointerEvent, 'pointerType'>): boolean {
  return e.pointerType === 'mouse';
}

export type Schwebe = {
  /** Die Maus kommt. `sofort` überspringt die Absicht — etwa, wenn schon offen ist. */
  readonly eintritt: (e: Pick<ReactPointerEvent, 'pointerType'>, sofort?: boolean) => void;
  /** Die Maus geht: Nach dem Nachlauf wird geschlossen, falls sie nicht zurückkommt. */
  readonly austritt: (e: Pick<ReactPointerEvent, 'pointerType'>) => void;
  /** Jede laufende Uhr anhalten — beim Klick, beim Schließen von anderer Seite. */
  readonly anhalten: () => void;
};

export function useSchwebe(aktionen: {
  readonly oeffnen: () => void;
  readonly schliessen: () => void;
}): Schwebe {
  /* Die Uhren rufen die Handlung von JETZT und nicht die vom Stellen der Uhr. */
  const aktuell = useRef(aktionen);
  useEffect(() => {
    aktuell.current = aktionen;
  });

  const uhr = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const anhalten = useCallback((): void => {
    if (uhr.current !== undefined) {
      clearTimeout(uhr.current);
      uhr.current = undefined;
    }
  }, []);
  useEffect(() => anhalten, [anhalten]);

  return useMemo(() => {
    const spaeter = (ms: number, was: () => void): void => {
      anhalten();
      uhr.current = setTimeout(() => {
        uhr.current = undefined;
        was();
      }, ms);
    };
    return {
      anhalten,
      eintritt(e, sofort = false) {
        if (!istMaus(e)) return;
        if (sofort) {
          anhalten();
          aktuell.current.oeffnen();
        } else {
          spaeter(ABSICHT_MS, () => aktuell.current.oeffnen());
        }
      },
      austritt(e) {
        if (!istMaus(e)) return;
        spaeter(NACHLAUF_MS, () => aktuell.current.schliessen());
      },
    };
  }, [anhalten]);
}
